import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { CalendarClock, ClipboardCheck, FileDown, Loader2, RefreshCw, Sparkles, Star, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api, background, changedElsewhere, streamAarText } from "../../api/client";
import type { Aar, AarIncident, AarTarget } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dialog, DialogBody } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { IncidentCode } from "../../components/IncidentCode";
import { motion, Presence, useClosing } from "../../components/presence";
import { Badge, Button, ButtonLink, Field, IconButton, SectionLabel, textareaClass } from "../../components/ui";
import { ViewHeader } from "../../components/ViewHeader";
import { unitDesignation } from "../../i18n/designation";
import { locale, t, tServer } from "../../i18n/i18n";
import { useTrialExport } from "../../licence/trial";
import { term } from "../../i18n/vocabulary";
import { effectiveOpo } from "../../map/units/useDeployment";
import { plural } from "../../plural";
import { licenceStore } from "../../state/licenceScope";
import { useUi } from "../../state/ui";
import { clearLocalCopy, readLocalCopy, useUnsavedDraft } from "../../state/unsavedDrafts";
import { STATUS_LABEL } from "../direx/common";
import { ExpectedActions } from "../direx/ExpectedActions";
import { AnnotationGroups } from "./AnnotationGroups";
import { DossierEmptyState } from "../DossierEmptyState";
import { OpoSelector } from "../order/OrderView";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { ZoneWaitState } from "../../map/zones/ZoneWaitCallout";
import { designateDossierReadOnly, useDossierLocked } from "../../sharing/access";
import { DossierReadOnlyNote } from "../../sharing/ReadOnlyBanner";
import { Waiting } from "../../components/Waiting";
import { translationInterval } from "../translation/TranslationBanner";
import { paddedRatings } from "./ratings";

/** 3A (analyse après action) : l'écran 3A de l'iPad — une carte par unité (le CO puis ses subordonnés), la note
 *  globale et le graphique des cinq critères, les incidents de la DIREX à noter ; la fiche d'évaluation montre
 *  l'incident, son objectif d'entraînement et la réaction attendue ; l'aide rédactionnelle est rédigée par le
 *  modèle local (prompts de l'iPad) et s'exporte en PDF « EVALOPS / CONTROLOPS ». */

const fmt = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? "—" : v.toLocaleString(locale(), { minimumFractionDigits: digits, maximumFractionDigits: digits });

function Stars({ value, onChange, size = 18 }: { value: number; onChange?: (v: number) => void; size?: number }) {
  return (
    <span className="flex items-center gap-0.5" role={onChange ? "radiogroup" : undefined}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= value;
        const star = <Star size={size} className={clsx("transition-colors", on ? "fill-rating text-rating" : "text-line-strong")} />;
        return onChange ? (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={t("{n} sur 5", { n })}
            onClick={() => onChange(value === n ? 0 : n)} className="rounded-md p-0.5 transition-colors hover:bg-hover">
            {star}
          </button>
        ) : (
          <span key={n}>{star}</span>
        );
      })}
    </span>
  );
}

/** Courbe des moyennes par critère (graphique de l'iPad), le plus faible en rouge. */
function CriteriaChart({ values, labels, weakest }: { values: (number | null)[]; labels: string[]; weakest: number | null }) {
  const W = 520;
  const H = 128;
  const left = 44; // de quoi loger la moitié d'un libellé aux deux bouts
  const right = 36;
  const top = 12;
  const bottom = 28;
  const step = (W - left - right) / (values.length - 1);
  const y = (v: number) => top + (1 - v / 5) * (H - top - bottom);
  const points = values.map((v, i) => (v === null ? null : { x: left + i * step, y: y(v), v, i }));
  const shown = points.filter((p): p is NonNullable<typeof p> => p !== null);
  const line = shown.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join("");
  const area = shown.length > 1 ? `${line}L${shown[shown.length - 1]!.x.toFixed(1)} ${y(0)}L${shown[0]!.x.toFixed(1)} ${y(0)}Z` : "";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-label={t("Moyenne de chaque critère sur 5")}>
      <defs>
        <linearGradient id="aar-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--color-accent)" stopOpacity="0.22" />
          <stop offset="1" stopColor="var(--color-accent)" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[1, 2, 3, 4, 5].map((g) => (
        <g key={g}>
          <line x1={left - 14} x2={W - right + 14} y1={y(g)} y2={y(g)} stroke="var(--color-line)" strokeWidth={1} />
          <text x={left - 20} y={y(g) + 3.5} textAnchor="end" style={{ fontSize: "var(--text-caps)" }} fill="var(--color-ink-muted)">{g}</text>
        </g>
      ))}
      {area && <path d={area} fill="url(#aar-area)" />}
      {shown.length > 1 && <path d={line} fill="none" stroke="var(--color-accent)" strokeWidth={2} strokeLinejoin="round" />}
      {shown.map((p) => (
        <circle key={p.i} cx={p.x} cy={p.y} r={p.i === weakest ? 5 : 3.5} fill={p.i === weakest ? "var(--color-danger)" : "var(--color-accent)"}
          stroke="var(--color-surface)" strokeWidth={1.5}>
          <title>{t("{label} : {value}/5", { label: term(labels[p.i]), value: fmt(p.v) })}</title>
        </circle>
      ))}
      {labels.map((l, i) => (
        <text key={l} x={left + i * step} y={H - 8} textAnchor="middle" style={{ fontSize: "var(--text-caps)" }}
          fill={i === weakest ? "var(--color-danger)" : "var(--color-ink-soft)"} fontWeight={i === weakest ? 600 : 500}>
          {term(l)}
        </text>
      ))}
    </svg>
  );
}

/** Copie locale d'une notation 3A non enregistrée : `generex-aar-draft:<dossier>:<OPO>:<incident>`. */
const AAR_DRAFT_PREFIX = "generex-aar-draft:";

interface NotationDraft {
  ratings: number[];
  remarks: string;
}

function EvaluationDialog({
  incident,
  current,
  criteria,
  busy,
  copyKey,
  onSave,
  onClose,
}: {
  /** Incident tel qu'il était à l'ouverture de la fiche (la note de départ de la saisie). */
  incident: AarIncident;
  /** Le même, relu depuis (toutes les 10 s) : une note modifiée par un autre poste pendant la saisie s'y lit. */
  current: AarIncident;
  criteria: string[];
  busy: boolean;
  /** Copie locale de la saisie (rangée sous la licence) : rétablie à l'ouverture si elle diffère de la note. */
  copyKey: string;
  /** `base` : date de la note sur laquelle porte l'enregistrement (null : pas encore notée). */
  onSave: (ratings: number[], remarks: string, base: string | null) => void;
  onClose: () => void;
}) {
  const saved: NotationDraft = {
    ratings: paddedRatings(incident.evaluation?.ratings, criteria.length),
    remarks: incident.evaluation?.remarks ?? "",
  };
  // Notation d'une session précédente (onglet fermé, licence perdue), différente de la note : rétablie avec un avis.
  const [restored] = useState(() => {
    const copy = readLocalCopy<NotationDraft>(copyKey);
    return copy && Array.isArray(copy.value.ratings) && JSON.stringify(copy.value) !== JSON.stringify(saved) ? copy : null;
  });
  const [ratings, setRatings] = useState<number[]>(() =>
    restored ? paddedRatings(restored.value.ratings, criteria.length) : saved.ratings,
  );
  const [remarks, setRemarks] = useState(restored?.value.remarks ?? saved.remarks);
  const notify = useUi((s) => s.notify);
  const criteriaRef = useRef<HTMLDivElement>(null);
  const empty = ratings.every((r) => r === 0) && !remarks.trim();
  useEffect(() => {
    if (restored) {
      notify({
        text: t("Saisie retrouvée"),
        detail: t("La notation non enregistrée de cet incident est rétablie : enregistrez-la ou annulez-la."),
        tone: "info",
      });
    }
  }, [restored, notify]);
  // Saisie non enregistrée : la fenêtre demande avant de se fermer d'un clic à côté ou d'Échap, la fermeture de
  // l'onglet aussi, et une copie locale est gardée (audit de persistance M4).
  const dirty = JSON.stringify({ ratings, remarks }) !== JSON.stringify(saved);
  // Note modifiée par un autre poste pendant la saisie : l'encadré le dit ; enregistrer remplace alors sa note en
  // connaissance de cause. Saisie rétablie : elle porte sur la note de sa session.
  const opened = restored ? ((restored.base as string | null) ?? null) : (incident.evaluation?.updated_at ?? null);
  useUnsavedDraft(`aar:${copyKey}`, dirty, {
    label: t("Notation 3A de l'incident {code}", { code: incident.code }),
    discard: onClose,
    reveal: () => designate(criteriaRef.current, "ring"),
    copy: { key: copyKey, value: { ratings, remarks }, base: opened },
  });
  const latest = current.evaluation?.updated_at ?? null;
  const stale = opened !== latest;
  const save = (values: number[], text: string) => onSave(values, text, stale ? latest : opened);
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : la note se consulte, rien ne s'enregistre.
  const locked = useDossierLocked();
  return (
    <Dialog
      title={t("Évaluation de l'incident")}
      subtitle={`${incident.code} — ${incident.short}`}
      onClose={onClose}
      dirty={dirty}
      width={620}
      footer={
        <>
          {incident.evaluation && (
            <Button
              variant="danger-ghost"
              className="mr-auto"
              onClick={() => save([0, 0, 0, 0, 0], "")}
              disabled={busy}
              blocked={locked}
              onBlocked={designateDossierReadOnly}
            >
              <Trash2 size={14} /> {t("Effacer la note")}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>{t("Annuler")}</Button>
          <Button
            onClick={() => save(ratings, remarks)}
            disabled={busy}
            blocked={locked || (empty && !incident.evaluation)}
            onBlocked={() => {
              if (locked) return designateDossierReadOnly();
              // Rien n'est noté : les critères sont l'action attendue (R12).
              designate(criteriaRef.current, "ring");
              notify({ text: t("Notez au moins un critère ou ajoutez une remarque."), tone: "info" });
            }}
          >
            {t("Enregistrer")}
          </Button>
        </>
      }
    >
      <DialogBody className="space-y-4">
        <DossierReadOnlyNote />
        <Presence show={stale}>
          {stale && (
            <Callout tone="warn" title={t("Note modifiée par un autre poste pendant votre saisie")}>
              {t("Votre saisie est gardée. Annuler affiche sa note ; Enregistrer la remplace par la vôtre.")}
            </Callout>
          )}
        </Presence>
        <div className="space-y-1.5 rounded-lg bg-canvas px-3.5 py-3">
          <div className="flex flex-wrap items-center gap-2 text-meta text-ink-muted">
            <IncidentCode kind={incident.type} code={incident.code} />
            {incident.tempo} · {term(incident.cellule)} · {t(STATUS_LABEL[incident.status])}
          </div>
          {incident.detail && <p className="text-ui leading-relaxed text-ink">{incident.detail}</p>}
          {incident.objective && (
            <p className="text-ui leading-relaxed text-ink-soft">
              <span className="font-semibold text-ink">{t("Objectif d'entraînement :")} </span>
              {incident.objective}
            </p>
          )}
          {incident.reaction && (
            <p className="text-ui leading-relaxed text-ink-soft">
              <span className="font-semibold text-ink">{t("Réaction attendue :")} </span>
              {incident.reaction}
            </p>
          )}
        </div>
        {/* Attendus de l'évaluateur (incidents drones) : dépliés, ce sur quoi la note se donne. */}
        {Boolean(incident.expected?.length) && (
          <div className="rounded-lg border border-line">
            <ExpectedActions items={incident.expected} title={t("Attendus de l'évaluateur")} defaultOpen />
          </div>
        )}
        <div ref={criteriaRef} className="divide-y divide-line rounded-lg border border-line">
          {criteria.map((label, k) => (
            <div key={label} className="flex min-h-11 items-center gap-3 px-3.5 py-1.5">
              <span className="flex-1 text-ui text-ink">{term(label)}</span>
              <Stars value={ratings[k] ?? 0} onChange={locked ? undefined : (v) => setRatings((r) => r.map((x, i) => (i === k ? v : x)))} />
            </div>
          ))}
        </div>
        <Field label={t("Remarques générales")}>
          <textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder={t("Commentaire libre…")} readOnly={locked}
            className={textareaClass} />
        </Field>
        <p className="text-meta text-ink-muted">
          {t("Un clic sur l'étoile déjà choisie retire la note du critère ; un critère non noté n'entre pas dans les moyennes.")}
        </p>
      </DialogBody>
    </Dialog>
  );
}

function AssistText({
  exerciseId,
  target,
  opo,
  onDone,
  onRateFirst,
}: {
  exerciseId: string;
  target: AarTarget;
  opo: number | undefined;
  onDone: () => void;
  /** Aucun incident noté : désigne le premier « À noter » de l'unité (R12). */
  onRateFirst: () => void;
}) {
  const [text, setText] = useState("");
  // Dossier anglais : l'IA rédige d'abord en français (consignes de l'iPad), puis traduit ; ce premier jet
  // n'est jamais montré.
  const [drafting, setDrafting] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);
  const canWrite = target.stats.rated > 0;
  // « Rédiger » enregistre le texte dans le dossier : bloqué en lecture seule, l'appui désigne le bandeau.
  const locked = useDossierLocked();
  const write = async () => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setRunning(true);
    setText("");
    setDrafting(false);
    setError(null);
    setSource(null);
    try {
      await streamAarText(
        exerciseId,
        target.key,
        opo,
        ({ event, data }) => {
          if (event === "source") setSource(data.label ?? null);
          else if (event === "token") {
            if (data.draft) setDrafting(true);
            else if (data.reset) setText("");
            else {
              setDrafting(false);
              setText((t) => t + (data.text ?? ""));
            }
          } else if (event === "done") {
            setDrafting(false);
            setText(data.text ?? "");
            onDone();
          } else if (event === "error") setError(tServer(data.message ?? t("Rédaction impossible.")));
        },
        controller.signal,
      );
    } catch (e) {
      if (!controller.signal.aborted) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (abort.current === controller) {
        setRunning(false);
        setDrafting(false);
      }
    }
  };
  const shown = running || text ? text : target.text;
  const closing = useClosing();
  return (
    <div className={clsx("space-y-2.5 border-t border-line px-5 py-4", motion("top", closing))}>
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-run" />
        <SectionLabel as="h3" inset>{t("Aide rédactionnelle 3A")}</SectionLabel>
        <Button
          size="sm"
          variant="secondary"
          className="ml-auto"
          onClick={write}
          disabled={running}
          blocked={locked || !canWrite}
          onBlocked={locked ? designateDossierReadOnly : onRateFirst}
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : shown ? <RefreshCw size={14} /> : <Sparkles size={14} />}
          {running ? <Waiting>{t("Rédaction…")}</Waiting> : shown ? t("Régénérer") : t("Rédiger")}
        </Button>
      </div>
      <Presence show={Boolean(error)}>{error && <Callout tone="danger">{error}</Callout>}</Presence>
      {running && (drafting || !text) ? (
        <p className="flex items-center gap-2 text-ui text-ink-muted">
          <Loader2 size={14} className="animate-spin" />
          {drafting ? <Waiting>{t("L'IA rédige l'aide, puis la traduit en anglais…")}</Waiting> : <Waiting>{t("L'IA prend connaissance des notes…")}</Waiting>}
        </p>
      ) : !running && !text && target.text_translating ? (
        // Aide rédigée dans l'autre langue (docs/13) : sa traduction arrive, attente animée à sa place.
        <p className="flex items-center gap-2 text-ui text-ink-muted">
          <Loader2 size={14} className="animate-spin" />
          <Waiting>{t("Traduction…")}</Waiting>
        </p>
      ) : shown ? (
        <p className="text-title leading-relaxed whitespace-pre-wrap text-ink">
          {shown}
          {running && <span className="briefing-caret" />}
        </p>
      ) : (
        <p className="text-ui text-ink-muted">
          {canWrite
            ? t("« Rédiger » : trois axes d'amélioration prioritaires, d'après les notes des incidents.")
            : t("Notez d'abord les incidents de l'unité.")}
        </p>
      )}
      {!running && (source || target.text_source) && (
        <p className="text-meta text-ink-muted">
          {t("Rédigée par le {source} d'après les notes : à relire avant diffusion.", { source: tServer(source ?? target.text_source ?? "") })}
        </p>
      )}
    </div>
  );
}

function TargetCard({
  target,
  data,
  exerciseId,
  opo,
  busy,
  language,
  onRate,
  onClear,
  onTextDone,
}: {
  target: AarTarget;
  /** Langue du dossier : césure des titres d'incident. */
  language: string;
  data: Aar;
  exerciseId: string;
  opo: number | undefined;
  busy: boolean;
  onRate: (incident: AarIncident) => void;
  onClear: () => void;
  onTextDone: () => void;
}) {
  const [assist, setAssist] = useState(Boolean(target.text));
  const locked = useDossierLocked();
  // Licence d'essai (registre F44) : EVALOPS grisé, quelle que soit l'autre raison.
  const trialExport = useTrialExport();
  const [confirm, setConfirm] = useState(false);
  const { stats } = target;
  const hasChart = stats.criteria.filter((c) => c !== null).length >= 2;
  const notify = useUi((s) => s.notify);
  const rateTarget = `aar-rate-${target.key}`;
  const direxTarget = `aar-direx-${target.key}`;
  const setView = useUi((s) => s.setView);
  const firstUnrated = target.incidents.find((i) => !i.evaluation)?.id;
  const nothing = stats.rated === 0 && !target.text;
  // Rien n'est noté pour l'unité : le premier incident « À noter » est l'action attendue (R12) ; sans incident
  // affecté, c'est « Ouvrir la DIREX », où l'on en affecte.
  const rateFirst = () => {
    if (target.incidents.length === 0) {
      designate(attentionSelector(direxTarget));
      notify({ text: t("Aucun incident affecté à cette unité : affectez-en dans la DIREX"), tone: "info" });
      return;
    }
    designate(attentionSelector(rateTarget));
    notify({ text: t("Notez au moins un incident de cette unité"), tone: "info" });
  };
  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-card">
      <header className="flex items-start gap-3 border-b border-line px-5 pt-4 pb-3.5">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-title font-semibold text-friend">
            {unitDesignation(target.label)}
            {target.commander && <span className="ml-1.5 font-medium text-ink-muted">{t("(CO)")}</span>}
          </h2>
          <p className="mt-0.5 text-meta text-ink-muted">
            {stats.total === 0
              ? t("Aucun incident affecté")
              : t("{rated} sur {total}", { rated: plural(stats.rated, t("incident noté"), t("incidents notés")), total: stats.total })}
          </p>
        </div>
        <div className="text-right">
          <SectionLabel inset>{t("Note globale")}</SectionLabel>
          <div className={clsx("text-stat leading-tight font-semibold tabular-nums", stats.overall === null ? "text-ink-muted" : stats.overall < 2.5 ? "text-danger" : stats.overall < 3.5 ? "text-warn" : "text-ok")}>
            {stats.overall === null ? "—" : `${fmt(stats.overall)}/5`}
          </div>
        </div>
      </header>
      {hasChart && (
        <div className="border-b border-line px-4 pt-2">
          <CriteriaChart values={stats.criteria} labels={data.chart_labels} weakest={stats.weakest} />
        </div>
      )}
      {target.incidents.length > 0 ? (
        // Grille de vignettes : toutes visibles, aucune coupée au bord (pas de défilement horizontal sans indice).
        <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2 px-5 py-3.5">
          {target.incidents.map((i) => {
            const given = (i.evaluation?.ratings ?? []).filter((r) => r > 0);
            const mean = given.length ? given.reduce((a, b) => a + b, 0) / given.length : null;
            return (
              <button key={i.id} type="button" onClick={() => onRate(i)} title={`${i.code} — ${i.short}`}
                data-attention={i.id === firstUnrated ? rateTarget : undefined}
                className={clsx("flex min-w-0 flex-col gap-1 rounded-lg border px-2.5 py-2 text-left transition-colors hover:bg-hover",
                  i.evaluation ? "border-line bg-surface" : "border-dashed border-line-strong bg-canvas/60")}>
                <span className="flex items-center gap-1.5">
                  <IncidentCode kind={i.type} code={i.code} />
                  <span className="font-mono text-meta text-ink-muted">{i.tempo}</span>
                </span>
                <span lang={language} className="line-clamp-2 text-meta leading-snug break-words hyphens-auto text-ink">{i.short}</span>
                {i.annotation && (
                  <span className="truncate text-meta text-ink-muted"
                    title={i.annotation.orphan ? t("Annotation retirée de la carte") : t("Proposé par l'annotation « {name} » de la carte", { name: i.annotation.label ?? "" })}>
                    {i.annotation.orphan ? t("Sans annotation") : t("Carte : {name}", { name: i.annotation.label ?? "" })}
                  </span>
                )}
                {/* Rotation CENTAC : FRAGO diffusé en réaction à l'incident (A118). */}
                {(i.fragos?.length ?? 0) > 0 && (
                  <span className="truncate text-meta text-ink-muted">
                    {t("Réaction : {orders}", { orders: (i.fragos ?? []).map((n) => t("FRAGO N°{n}", { n })).join(", ") })}
                  </span>
                )}
                <span className="mt-auto flex items-center justify-between">
                  {mean !== null ? <Stars value={Math.round(mean)} size={12} /> : <span className="text-meta font-medium text-accent">{t("À noter")}</span>}
                  {mean !== null && <span className="text-meta font-semibold text-ink tabular-nums">{fmt(mean)}</span>}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5">
          <p className="min-w-0 flex-1 text-ui text-ink-muted">{t("Aucun incident de la DIREX n'est affecté à cette unité.")}</p>
          <Button size="sm" variant="secondary" data-attention={direxTarget} onClick={() => setView("timeline")}>
            <CalendarClock size={14} /> {t("Ouvrir la DIREX")}
          </Button>
        </div>
      )}
      {/* Pied collé au bas de la carte : alignés d'une carte à l'autre quand une rangée prend la hauteur de la plus
          haute. */}
      <footer className="mt-auto flex flex-wrap items-center gap-1.5 border-t border-line px-4 py-2">
        <Button
          size="sm"
          variant={assist ? "secondary" : "ghost"}
          aria-pressed={assist}
          onClick={() => setAssist((a) => !a)}
          blocked={stats.rated === 0 && !target.text && !assist}
          onBlocked={rateFirst}
        >
          <Sparkles size={14} /> {t("Aide rédactionnelle")}
        </Button>
        {nothing ? (
          <Button size="sm" variant="ghost" blocked onBlocked={trialExport.blocked ? trialExport.onBlocked : rateFirst}
            title={trialExport.title ?? t("Export PDF « EVALOPS / CONTROLOPS » : aide rédactionnelle, notes et remarques")}>
            <FileDown size={14} /> {t("EVALOPS (PDF)")}
          </Button>
        ) : (
          <ButtonLink size="sm" variant="ghost" href={api.evalopsUrl(exerciseId, target.key, opo)}
            blocked={trialExport.blocked} onBlocked={trialExport.onBlocked}
            title={trialExport.title ?? t("Export PDF « EVALOPS / CONTROLOPS » : aide rédactionnelle, notes et remarques")}>
            <FileDown size={14} /> {t("EVALOPS (PDF)")}
          </ButtonLink>
        )}
        <IconButton
          size="sm"
          variant="danger"
          className="ml-auto"
          onClick={() => setConfirm(true)}
          disabled={busy || nothing}
          blocked={locked}
          onBlocked={designateDossierReadOnly}
          title={t("Effacer les notes et l'aide rédactionnelle de l'unité")}
          aria-label={t("Effacer les notes et l'aide rédactionnelle de l'unité")}
        >
          <Trash2 size={14} />
        </IconButton>
      </footer>
      <Presence show={assist}>
        {assist && <AssistText exerciseId={exerciseId} target={target} opo={opo} onDone={onTextDone} onRateFirst={rateFirst} />}
      </Presence>
      <Presence show={confirm}>
        {confirm && (
          <ConfirmDialog title={t("Effacer la 3A de cette unité ?")} confirmLabel={t("Effacer")} busy={busy} busyLabel={t("Effacement…")}
            onConfirm={() => { setConfirm(false); onClear(); }} onCancel={() => setConfirm(false)}>
            {t("Les notes, remarques et l'aide rédactionnelle de « {unit} » seront effacées.", { unit: unitDesignation(target.label) })}
          </ConfirmDialog>
        )}
      </Presence>
    </section>
  );
}

export function AarView() {
  const exerciseId = useUi((s) => s.exerciseId);
  const params = useUi((s) => s.deploymentParams);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const mapOpo = useUi((s) => s.opo);
  const setOpo = useUi((s) => s.setOpo);
  const queryClient = useQueryClient();
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const complete = Boolean(exercise?.map_slug && exercise.parameters.level && exercise.parameters.odb);
  const centac = exercise?.parameters.level === "CENTAC";
  const opo = centac ? effectiveOpo(params, mapOpo) : undefined;
  const key = ["aar", exerciseId, opo ?? 1] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => background(() => api.aar(exerciseId as string, opo)),
    enabled: Boolean(exerciseId && complete),
    // Aide rédactionnelle lue dans l'autre langue (docs/13) : relue toutes les 2,5 s tant qu'une traduction attend.
    refetchInterval: (q) => translationInterval(Boolean(q.state.data?.targets.some((target) => target.text_translating)), 10_000),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const data = query.data;
  const [rating, setRating] = useState<AarIncident | null>(null);
  // Notation d'une session précédente non enregistrée : sa fiche se rouvre d'elle-même, une fois par dossier et OPO.
  const aarCopyPrefix = `${AAR_DRAFT_PREFIX}${exerciseId ?? ""}:${opo ?? 1}:`;
  const restoredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || !exerciseId || restoredFor.current === aarCopyPrefix) return;
    restoredFor.current = aarCopyPrefix;
    const incidents = data.targets.flatMap((target) => target.incidents);
    for (const key of licenceStore.keys()) {
      if (!key.startsWith(aarCopyPrefix)) continue;
      const target = incidents.find((i) => i.id === key.slice(aarCopyPrefix.length));
      const copy = readLocalCopy<NotationDraft>(key);
      const note = { ratings: paddedRatings(target?.evaluation?.ratings, data.criteria.length), remarks: target?.evaluation?.remarks ?? "" };
      if (!target || !copy || JSON.stringify(copy.value) === JSON.stringify(note)) {
        clearLocalCopy(key);
        continue;
      }
      setRating(target);
      break;
    }
  }, [data, exerciseId, aarCopyPrefix]);
  const mutation = useMutation({
    mutationFn: (run: () => Promise<Aar>) => run(),
    onSuccess: (next) => queryClient.setQueryData(key, next),
    onError: (e: Error) => {
      // Note modifiée entre-temps par un autre poste : rien n'est écrasé, la fiche reste ouverte, la 3A est relue.
      if (changedElsewhere(e)) {
        notify({ text: t("Modifié par un autre poste"), detail: e.message, tone: "warn" });
        void queryClient.invalidateQueries({ queryKey: key });
        return;
      }
      notify({ text: t("Action impossible"), detail: e.message, tone: "error" });
    },
  });
  const busy = mutation.isPending;
  const totals = data?.totals;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="evaluation"
        context={exercise?.name}
        badges={
          <>
            {totals && totals.total > 0 && (
              <Badge tone={totals.rated === totals.total ? "ok" : "neutral"} title={t("Incidents notés sur les incidents affectés")}>
                {t("{rated}/{total} notés", { rated: totals.rated, total: totals.total })}
              </Badge>
            )}
            {totals?.overall != null && <Badge tone="accent">{t("Moyenne {value}/5", { value: fmt(totals.overall) })}</Badge>}
          </>
        }
        actions={centac && opo ? <OpoSelector value={opo} onChange={setOpo} /> : null}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!exerciseId ? (
          <DossierEmptyState view="evaluation" kind="none">
            {t("La 3A note le traitement des incidents de la DIREX par chaque unité.")}
          </DossierEmptyState>
        ) : !complete ? (
          <DossierEmptyState view="evaluation" kind="incomplete">
            {t("La 3A note le traitement des incidents de la DIREX par chaque unité.")}
          </DossierEmptyState>
        ) : query.isError ? (
          zoneRefusalOf(query.error) ? (
            <ZoneWaitState message={query.error.message} error={query.error} />
          ) : (
            <EmptyState variant="error">{query.error.message}</EmptyState>
          )
        ) : !data ? (
          <EmptyState variant="loading" title={t("Chargement de la 3A…")} />
        ) : data.totals.total === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title={t("Aucun incident à noter")}
            action={
              <Button onClick={() => setView("timeline")} highlight>
                <CalendarClock size={14} /> {t("Ouvrir la DIREX")}
              </Button>
            }
          >
            {t("Aucun incident de la DIREX n'est affecté à une unité : générez ou créez des incidents dans la DIREX, puis notez ici leur traitement.")}
          </EmptyState>
        ) : (
          <div className={clsx("@container mx-auto w-full max-w-[1280px] space-y-4 px-6 py-6 max-sm:px-4", motion("fade"))}>
            <p className="text-ui leading-relaxed text-ink-soft">
              {t("Chaque incident se note sur cinq critères, de une à cinq étoiles : prise en compte par le CO, délai d'étude et de décision, diffusion au sein du CO, compte rendu vers l'échelon supérieur, diffusion aux subordonnés.")}
              {totals &&
                totals.unassigned > 0 &&
                ` ${plural(
                  totals.unassigned,
                  t("incident « éventuel », sans cible, n'est pas noté."),
                  t("incidents « éventuels », sans cible, ne sont pas notés."),
                )}`}
              {totals &&
                (totals.removed ?? 0) > 0 &&
                ` ${plural(
                  totals.removed ?? 0,
                  t("incident d'une unité retirée du dossier n'est pas noté : il reste visible dans la DIREX."),
                  t("incidents d'unités retirées du dossier ne sont pas notés : ils restent visibles dans la DIREX."),
                )}`}
            </p>
            <div className="grid gap-5 @4xl:grid-cols-2">
              {data.targets.map((t) => (
                <TargetCard
                  key={t.key}
                  target={t}
                  data={data}
                  exerciseId={exerciseId}
                  opo={opo}
                  busy={busy}
                  language={exercise?.language === "en" ? "en" : "fr"}
                  onRate={setRating}
                  onClear={() => mutation.mutate(() => api.clearAarTarget(exerciseId, t.key, opo))}
                  onTextDone={() => void queryClient.invalidateQueries({ queryKey: key })}
                />
              ))}
            </div>
            <AnnotationGroups data={data} onRate={setRating} />
          </div>
        )}
      </div>
      <Presence show={Boolean(rating && data)}>
        {rating && data && (
          <EvaluationDialog
            incident={rating}
            current={data.targets.flatMap((target) => target.incidents).find((i) => i.id === rating.id) ?? rating}
            criteria={data.criteria}
            busy={busy}
            copyKey={`${aarCopyPrefix}${rating.id}`}
            onClose={() => setRating(null)}
            onSave={(ratings, remarks, base) =>
              mutation.mutate(() => api.evaluateIncident(exerciseId as string, rating.id, ratings, remarks, base, opo), {
                onSuccess: () => setRating(null),
              })
            }
          />
        )}
      </Presence>
    </div>
  );
}
